/**
 * /why-zenzest — single-page value-prop landing.
 * Replaces the prior /compare + /vs/{slug} content (removed for legal /
 * trademark-safety reasons — naming third-party licensees in marketing
 * runs into NY OCM ad rules + nominative-fair-use boundaries). All claims
 * here are about ZenZest only.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Check, MapPin, Sparkles, Star } from "lucide-react";

import SEOHead from "../components/SEOHead";
import SchemaJsonLd from "../components/SchemaJsonLd";
import LiveReviewBadge from "../components/LiveReviewBadge";
import { useLiveAggregateReviews } from "../lib/useLiveReviews";
import { breadcrumbSchema, faqSchema } from "../lib/seo";

const REASONS = [
    "Two NY OCM-licensed flagships — Queens / Long Island (272-06 Union Tpke) and Staten Island (2343 Forest Ave).",
    "One of the largest legal cannabis catalogs in NY — 2,800+ live SKUs daily, 180+ brands.",
    "Free same-day delivery to 80+ NY metro ZIPs on $100+ orders.",
    "ZenCircle loyalty from order #1 — 5 tiers, 8 redemption rungs, no signup gates.",
    "Stackable specials — multiple BOGO / % off / mix-and-match deals on the same cart.",
    "Live AI budtender chat — pairs strain to mood, budget, or effect; can add to cart for you.",
    "Verified Google reviews across both flagships (live count + rating on every page).",
];

const FAQS = [
    {
        q: "Where is ZenZest located?",
        a: "Two NY OCM-licensed flagships: 272-06 Union Tpke (Queens / Long Island, on the Nassau border) and 2343 Forest Ave (Staten Island). Both offer in-store pickup and free same-day delivery.",
    },
    {
        q: "Does ZenZest deliver to my address?",
        a: "ZenZest delivers free across 80+ NY metro ZIPs on orders of $100+. Drop your ZIP at /cannabis-delivery to confirm the exact ETA window.",
    },
    {
        q: "Is ZenZest a licensed New York dispensary?",
        a: "Yes. Both flagships are NY Office of Cannabis Management (OCM) licensed adult-use dispensaries. Every product is lab-tested with a published Certificate of Analysis.",
    },
    {
        q: "How does ZenCircle loyalty work?",
        a: "ZenCircle is ZenZest's loyalty program — five tiers from 1× to 2× points multipliers, 8 redemption rungs starting at 100 points = $5 off and scaling to 3,000 points = $250 off. You earn from order #1 automatically.",
    },
    {
        q: "How many cannabis brands and strains does ZenZest carry?",
        a: "Around 180 NY-licensed brands and 230+ unique cannabis strains in stock daily. Browse the live menu at /menu/queens-long-island or the strain library at /strains/queens-long-island.",
    },
];

export default function WhyZenZest() {
    const live = useLiveAggregateReviews(["queens-long-island", "staten-island"]);
    const canonical = "https://weeddispensarynearme.com/why-zenzest";

    return (
        <>
            <SEOHead
                title="Why ZenZest — NY's Best Cannabis Dispensary"
                description="Two NY OCM-licensed flagships, 2,800+ live cannabis SKUs, free same-day delivery to 80+ NY metro ZIPs, ZenCircle loyalty, and verified Google reviews. The full picture on what makes ZenZest different."
                canonical={canonical}
            />
            <SchemaJsonLd
                schema={[
                    faqSchema(FAQS),
                    breadcrumbSchema([
                        { name: "Home", url: "https://weeddispensarynearme.com/" },
                        { name: "Why ZenZest", url: canonical },
                    ]),
                ]}
            />

            <section className="bg-zen-elevated text-zen-cream">
                <div className="max-w-[1200px] mx-auto px-6 sm:px-10 pt-24 pb-14">
                    <p className="text-[11px] uppercase tracking-[0.35em] text-zen-olive-light mb-4 inline-flex items-center gap-3">
                        <span className="w-8 h-px bg-zen-olive-light" />
                        Why ZenZest
                    </p>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight mb-6 text-white max-w-4xl">
                        NY&apos;s cannabis dispensary,{" "}
                        <span className="italic text-zen-olive-light">done well.</span>
                    </h1>
                    <p className="text-white/80 text-base sm:text-lg max-w-2xl leading-relaxed mb-7">
                        Family-owned. Two NY OCM-licensed flagships. The biggest live menu on Long Island.
                        Free same-day delivery to 80+ NY metro ZIPs. Built around you, not the other way around.
                    </p>
                    <div className="flex flex-wrap items-center gap-6">
                        <LiveReviewBadge tone="light" testid="why-live-reviews" />
                        <span className="text-[11px] uppercase tracking-[0.25em] text-zen-cream-muted">
                            NY OCM-licensed · Two flagships · 80+ delivery ZIPs
                        </span>
                    </div>
                </div>
            </section>

            <section className="bg-zen-bg border-b border-zen-line">
                <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-16">
                    <h2 className="font-serif text-3xl sm:text-4xl text-zen-ink font-light leading-tight mb-8 max-w-3xl">
                        The {REASONS.length} things shoppers always mention.
                    </h2>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {REASONS.map((r) => (
                            <li key={r} className="flex items-start gap-3 bg-zen-surface border border-zen-line p-5">
                                <Check size={16} strokeWidth={2.5} className="text-zen-olive shrink-0 mt-0.5" />
                                <span className="text-sm text-zen-ink leading-relaxed">{r}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-zen-ink/10 border border-zen-ink/10">
                        <Stat icon={<Sparkles size={18} className="text-zen-olive" />} value="2,800+" label="SKUs live daily" />
                        <Stat icon={<MapPin size={18} className="text-zen-olive" />} value="80+" label="Free-delivery ZIPs" />
                        <Stat
                            icon={<Star size={18} className="text-zen-olive" />}
                            value={`${Number(live.rating).toFixed(1)}★`}
                            label={`${Number(live.count || 0).toLocaleString()}+ Google reviews`}
                        />
                    </div>
                </div>
            </section>

            <section className="bg-zen-surface border-b border-zen-line">
                <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-16">
                    <h2 className="font-serif text-3xl sm:text-4xl text-zen-ink font-light leading-tight mb-8 max-w-3xl">
                        Frequently asked.
                    </h2>
                    <dl className="divide-y divide-zen-line">
                        {FAQS.map((f) => (
                            <div key={f.q} className="py-5">
                                <dt className="font-serif text-xl text-zen-ink mb-2 leading-snug">{f.q}</dt>
                                <dd className="text-zen-ink-soft text-sm leading-relaxed max-w-3xl">{f.a}</dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </section>

            <section className="bg-zen-bg">
                <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-20 text-center">
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-zen-ink font-light leading-tight mb-6 max-w-3xl mx-auto">
                        Try ZenZest <span className="italic text-zen-olive">today.</span>
                    </h2>
                    <div className="flex flex-wrap justify-center gap-3">
                        <Link
                            to="/menu/queens-long-island"
                            className="bg-zen-olive hover:bg-zen-olive-dark text-zen-bg px-8 py-4 text-[11px] uppercase tracking-[0.25em] font-medium transition-colors inline-flex items-center gap-2"
                        >
                            Shop Queens / Long Island <ArrowRight size={13} />
                        </Link>
                        <Link
                            to="/menu/staten-island"
                            className="border border-zen-ink/30 text-zen-ink px-8 py-4 text-[11px] uppercase tracking-[0.25em] font-medium hover:bg-zen-ink hover:text-zen-cream transition-colors inline-flex items-center gap-2"
                        >
                            Shop Staten Island
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}

function Stat({ icon, value, label }) {
    return (
        <div className="bg-zen-bg p-6 flex flex-col gap-3 items-start">
            {icon}
            <div className="font-serif text-4xl text-zen-ink font-light leading-none">{value}</div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-zen-ink-soft">{label}</div>
        </div>
    );
}
